import React, { useState } from 'react';
import { Code2, Copy, Check, Smartphone, FolderTree, Layers, ExternalLink } from 'lucide-react';

export const ExpoCodeView: React.FC = () => {
  const [copiedFile, setCopiedFile] = useState<string | null>(null);
  const [activeCodeFile, setActiveCodeFile] = useState<'app.json' | 'api.ts' | 'BillingScreen.tsx' | 'DuitkuWebView.tsx'>('BillingScreen.tsx');

  const copyToClipboard = (fileName: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedFile(fileName);
    setTimeout(() => setCopiedFile(null), 2000);
  };

  const codeSnippets: Record<string, { title: string; lang: string; code: string }> = {
    'BillingScreen.tsx': {
      title: 'src/screens/BillingScreen.tsx (Expo React Native)',
      lang: 'typescript',
      code: `import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, TouchableOpacity, StyleSheet, Alert, ActivityIndicator } from 'react-native';
import { apiClient } from '../api/client';
import { TagihanSiswa } from '../types';

export const BillingScreen = ({ route, navigation }: any) => {
  const { studentId } = route.params;
  const [bills, setBills] = useState<TagihanSiswa[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBills();
  }, [studentId]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(\`/billing/student/\${studentId}\`);
      setBills(res.data.filter((b: TagihanSiswa) => b.status !== 'PAID'));
    } catch (err: any) {
      Alert.alert('Error', 'Gagal memuat data tagihan');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCheckout = () => {
    if (selectedIds.length === 0) {
      Alert.alert('Peringatan', 'Pilih minimal satu tagihan untuk dibayar');
      return;
    }
    navigation.navigate('PaymentGateway', {
      studentId,
      billIds: selectedIds,
    });
  };

  return (
    <View style={styles.container}>
      <Text style={styles.headerTitle}>Tagihan Siswa SDMT</Text>
      {loading ? (
        <ActivityIndicator size="large" color="#0284c7" />
      ) : (
        <FlatList
          data={bills}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <TouchableOpacity
                onPress={() => toggleSelect(item.id)}
                style={[styles.billCard, isSelected && styles.billCardSelected]}
              >
                <View style={styles.billInfo}>
                  <Text style={styles.billName}>{item.namaPos} {item.bulan ? \`- \${item.bulan}\` : ''}</Text>
                  <Text style={styles.billSub}>T.A: {item.tahunAjaran}</Text>
                </View>
                <Text style={styles.billAmount}>Rp {item.sisa.toLocaleString('id-ID')}</Text>
              </TouchableOpacity>
            );
          }}
        />
      )}

      <TouchableOpacity
        onPress={handleCheckout}
        disabled={selectedIds.length === 0}
        style={[styles.payBtn, selectedIds.length === 0 && styles.payBtnDisabled]}
      >
        <Text style={styles.payBtnText}>Bayar ({selectedIds.length} Tagihan)</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc', padding: 16 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: '#0f172a', marginBottom: 16 },
  billCard: { backgroundColor: '#ffffff', padding: 16, borderRadius: 12, marginBottom: 10, borderWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  billCardSelected: { borderColor: '#0284c7', backgroundColor: '#f0f9ff' },
  billInfo: { flex: 1 },
  billName: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  billSub: { fontSize: 12, color: '#64748b', marginTop: 2 },
  billAmount: { fontSize: 14, fontWeight: '800', color: '#e11d48' },
  payBtn: { backgroundColor: '#059669', paddingVertical: 14, borderRadius: 12, alignItems: 'center', marginTop: 12 },
  payBtnDisabled: { backgroundColor: '#cbd5e1' },
  payBtnText: { color: '#ffffff', fontSize: 14, fontWeight: 'bold' }
});`,
    },
    'DuitkuWebView.tsx': {
      title: 'src/screens/DuitkuWebView.tsx (WebView Gateway Runner)',
      lang: 'typescript',
      code: `import React from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { WebView } from 'react-native-webview';

interface Props {
  route: any;
  navigation: any;
}

export const DuitkuWebViewScreen: React.FC<Props> = ({ route, navigation }) => {
  const { paymentUrl } = route.params;

  const handleNavigationStateChange = (navState: any) => {
    // Check if redirect matches callback or return URL
    if (navState.url.includes('/api/gateways/duitku/callback') || navState.url.includes('status=success')) {
      navigation.navigate('PaymentSuccess');
    }
  };

  return (
    <View style={styles.container}>
      <WebView
        source={{ uri: paymentUrl }}
        onNavigationStateChange={handleNavigationStateChange}
        startInLoadingState={true}
        renderLoading={() => <ActivityIndicator size="large" color="#0284c7" style={styles.loader} />}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  loader: { position: 'absolute', top: '50%', left: '50%', transform: [{ translateX: -15 }, { translateY: -15 }] }
});`,
    },
    'api.ts': {
      title: 'src/api/client.ts (Mobile Network Layer)',
      lang: 'typescript',
      code: `import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';

// In Expo development, use your computer IP or production backend URL:
export const API_BASE_URL = 'https://sdmt-payment.app/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(async (config) => {
  const token = await AsyncStorage.getItem('user_token');
  if (token) {
    config.headers.Authorization = \`Bearer \${token}\`;
  }
  return config;
});`,
    },
    'app.json': {
      title: 'app.json (Expo Manifest for Android & iOS)',
      lang: 'json',
      code: `{
  "expo": {
    "name": "SDMT Mobile Payment",
    "slug": "sdmt-mobile-payment",
    "version": "2.4.0",
    "orientation": "portrait",
    "icon": "./assets/icon.png",
    "userInterfaceStyle": "light",
    "splash": {
      "image": "./assets/splash.png",
      "resizeMode": "contain",
      "backgroundColor": "#0369a1"
    },
    "ios": {
      "supportsTablet": true,
      "bundleIdentifier": "id.sch.sdmt.payment"
    },
    "android": {
      "adaptiveIcon": {
        "foregroundImage": "./assets/adaptive-icon.png",
        "backgroundColor": "#0369a1"
      },
      "package": "id.sch.sdmt.payment",
      "permissions": [
        "INTERNET",
        "NOTIFICATIONS"
      ]
    },
    "plugins": [
      "expo-camera"
    ]
  }
}`,
    },
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Banner */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Arsitektur & Kode Expo Mobile (React Native)</h2>
          <p className="text-xs text-slate-500">
            Struktur modular untuk kemudahan ekspansi ke Android (Google Play) dan iOS (App Store)
          </p>
        </div>

        {/* File Tabs */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg border text-xs">
          {(['BillingScreen.tsx', 'DuitkuWebView.tsx', 'api.ts', 'app.json'] as const).map((fname) => (
            <button
              key={fname}
              onClick={() => setActiveCodeFile(fname)}
              className={`px-3 py-1.5 rounded-md font-semibold transition ${
                activeCodeFile === fname ? 'bg-sky-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {fname}
            </button>
          ))}
        </div>
      </div>

      {/* Code Display Card */}
      <div className="bg-slate-900 rounded-xl shadow-lg border border-slate-800 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-950 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Code2 className="w-4 h-4 text-amber-400" />
            <span className="font-mono text-xs font-semibold text-slate-200">
              {codeSnippets[activeCodeFile].title}
            </span>
          </div>

          <button
            onClick={() => copyToClipboard(activeCodeFile, codeSnippets[activeCodeFile].code)}
            className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs px-2.5 py-1 rounded transition"
          >
            {copiedFile === activeCodeFile ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-300">Tersalin!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Salin Kode</span>
              </>
            )}
          </button>
        </div>

        <pre className="p-4 text-xs font-mono text-slate-300 overflow-x-auto max-h-[480px] leading-relaxed">
          <code>{codeSnippets[activeCodeFile].code}</code>
        </pre>
      </div>

      {/* Modular Folder Structure Explanation */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-3">
        <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
          <FolderTree className="w-4 h-4 text-sky-600" />
          <span>Struktur Folder Modular Expo & Backend API</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
            <span className="font-bold text-sky-900 block">📁 Backend (Express + Gateway Engine)</span>
            <ul className="space-y-1 text-slate-600 font-mono text-[11px]">
              <li>├── /server/controllers/ (Auth, Master, Billing, Payment, Savings, Gateway)</li>
              <li>├── /server/gateways/ (DuitkuService, MidtransService dengan hashing)</li>
              <li>├── /server/database/ (In-memory seeded database, ready to Prisma/PostgreSQL)</li>
              <li>└── /server/routes/ (Modular REST API endpoints)</li>
            </ul>
          </div>

          <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
            <span className="font-bold text-emerald-900 block">📱 Mobile App (Expo SDK 52)</span>
            <ul className="space-y-1 text-slate-600 font-mono text-[11px]">
              <li>├── /src/screens/ (HomeScreen, BillingScreen, PaymentGateway, ReceiptScreen)</li>
              <li>├── /src/components/ (VirtualAccountCard, BillItemRow, QrCodeModal)</li>
              <li>├── /src/api/client.ts (Network client consuming the exact same backend)</li>
              <li>└── /src/types/index.ts (Shared type-safety definitions)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
